import React, { useState, useRef } from 'react';
import { Gauge } from 'lucide-react';
import { TokenUsage } from '../types';

interface ContextUsageBadgeProps {
  tokenUsage: TokenUsage;
}

function getUsageColor(pct: number) {
  if (pct < 30) return { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'text-blue-500', bar: 'bg-blue-500' };
  if (pct < 60) return { bg: 'bg-green-100', text: 'text-green-700', icon: 'text-green-500', bar: 'bg-green-500' };
  if (pct < 80) return { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: 'text-yellow-500', bar: 'bg-yellow-500' };
  return { bg: 'bg-red-100', text: 'text-red-700', icon: 'text-red-500', bar: 'bg-red-500' };
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function formatExact(n: number): string {
  return n.toLocaleString();
}

export function ContextUsageBadge({ tokenUsage }: ContextUsageBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const pct = tokenUsage.contextUsagePercent;
  const colors = getUsageColor(pct);

  const handleOpen = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 8,
        left: Math.max(8, rect.right - 280),
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="inline-block" data-testid="context-usage-badge">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${colors.bg} ${colors.text} hover:opacity-80 transition-opacity`}
        title={`Context usage: ${pct}% (${formatTokens(tokenUsage.peakPromptTokens)} / ${formatTokens(tokenUsage.contextLimit)})`}
      >
        <Gauge className={`h-2.5 w-2.5 ${colors.icon}`} />
        {pct}%
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setIsOpen(false)} />
          <div
            className="fixed z-[101] w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs text-gray-700"
            style={{ top: position.top, left: position.left, transform: 'translateY(-100%)' }}
            data-testid="context-usage-tooltip"
          >
            <div className="font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
              <Gauge className={`h-3.5 w-3.5 ${colors.icon}`} />
              Context Usage — {pct}%
            </div>

            <div className="mb-2">
              <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
                <span>Peak: {formatExact(tokenUsage.peakPromptTokens)} tokens ({tokenUsage.peakPromptAgent})</span>
                <span>{formatExact(tokenUsage.contextLimit)} limit</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full ${colors.bar}`} style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
            </div>

            <div className="text-[10px] text-gray-500 space-y-0.5">
              <div className="flex justify-between">
                <span>Total tokens this turn:</span>
                <span className="font-medium text-gray-700">{formatTokens(tokenUsage.totalTokens)}</span>
              </div>
              <div className="flex justify-between">
                <span>Prompt:</span>
                <span>{formatTokens(tokenUsage.totalPromptTokens)}</span>
              </div>
              <div className="flex justify-between">
                <span>Completion:</span>
                <span>{formatTokens(tokenUsage.totalCompletionTokens)}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
